import { motion, useInView, AnimatePresence } from "framer-motion";
import { useRef, useState, useEffect } from "react";
import { Ticket, Bell, Check } from "lucide-react";

interface TicketsSectionProps {
  preloaderComplete?: boolean;
}

const TicketsSection = ({ preloaderComplete = true }: TicketsSectionProps) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const shouldAnimate = isInView && preloaderComplete;
  
  const fullMessage = "The AI isn't booking appointments correctly...";
  const [typedText, setTypedText] = useState("");
  const [animationPhase, setAnimationPhase] = useState<
    "idle" | "typing" | "hovering" | "submitted" | "dashboard-update" | "complete"
  >("idle");
  const [showDashboardTicket, setShowDashboardTicket] = useState(false);
  const [showNewBadge, setShowNewBadge] = useState(false);

  // Animation loop
  useEffect(() => {
    if (!isInView || !preloaderComplete) return;

    const runAnimation = async () => {
      // Reset state
      setTypedText("");
      setAnimationPhase("idle");
      setShowDashboardTicket(false);
      setShowNewBadge(false);

      // Wait before starting
      await sleep(800);

      // Phase 1: Typewriter effect
      setAnimationPhase("typing");
      for (let i = 0; i <= fullMessage.length; i++) {
        setTypedText(fullMessage.slice(0, i));
        // Natural typing speed with small variations
        const delay = i === 0 ? 100 : Math.random() * 40 + 35;
        // Add slightly longer pauses after punctuation
        const extraPause = fullMessage[i - 1] === "'" || fullMessage[i - 1] === " " ? 20 : 0;
        await sleep(delay + extraPause);
      }

      // Phase 2: Button hover then click
      await sleep(400);
      setAnimationPhase("hovering");
      await sleep(600);
      setAnimationPhase("submitted");

      // Phase 3: Show success state briefly
      await sleep(1200);

      // Phase 4: Update dashboard
      setAnimationPhase("dashboard-update");
      setShowDashboardTicket(true);
      await sleep(300);
      setShowNewBadge(true);

      // Phase 5: Hold complete state
      await sleep(2000);
      setAnimationPhase("complete");

      // Wait before looping
      await sleep(1000);

      // Loop
      runAnimation();
    };

    runAnimation();

    return () => {
      // Cleanup handled by component unmount
    };
  }, [isInView]);

  const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  return (
    <section id="platform" ref={ref} className="relative py-16 md:py-32 px-4 md:px-6 overflow-hidden">
      {/* Minimal green gradient background - centered */}
      <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse at center, rgba(20, 184, 166, 0.10) 0%, transparent 60%)' }} />
      <div className="container mx-auto max-w-6xl relative z-10">
        <div className="grid lg:grid-cols-2 gap-8 md:gap-16 items-center">
          {/* Left: Text */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5 }}
              className="flex items-center gap-2 mb-3 md:mb-4"
            >
              <Ticket className="h-4 w-4 text-primary" />
              <span className="text-xs md:text-sm text-muted-foreground uppercase tracking-wider font-medium">
                Built for Retention
              </span>
            </motion.div>

            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="text-2xl sm:text-3xl md:text-5xl font-bold text-foreground leading-tight mb-4 md:mb-6"
            >
              Issues? They submit a <span className="ayra-accent">ticket</span>.
              <br />
              You see it instantly.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="text-base md:text-lg text-muted-foreground leading-relaxed"
            >
              No more group chats. No buried emails. Clients report problems directly in the platform. Fix fast. Retain longer.
            </motion.p>
          </div>

          {/* Right: Visual */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={shouldAnimate ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="relative lg:order-last"
          >
            <div className="bg-card/80 backdrop-blur-sm rounded-xl md:rounded-2xl p-4 md:p-6 border border-teal-500/8 dark:border-teal-400/12 shadow-xl">
              {/* Client Side */}
              <div className="mb-4 md:mb-6">
                <div className="text-[10px] md:text-xs text-muted-foreground mb-2 md:mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-blue-500" />
                  Client View
                </div>
                <div className="bg-secondary/50 rounded-lg md:rounded-xl p-3 md:p-4">
                  <div className="text-xs md:text-sm font-medium text-foreground mb-2">Report an Issue</div>
                  <motion.div
                    className="h-16 md:h-20 bg-background rounded-lg border border-border mb-2 md:mb-3 p-2 text-xs md:text-sm text-muted-foreground relative overflow-hidden"
                    animate={{
                      borderColor: animationPhase === "typing" ? "hsl(var(--primary))" : "hsl(var(--border))",
                    }}
                    transition={{ duration: 0.2 }}
                  >
                    <span className="text-xs md:text-sm">{typedText}</span>
                    {animationPhase === "typing" && (
                      <motion.span
                        className="inline-block w-0.5 h-3 md:h-4 bg-foreground ml-0.5 align-middle"
                        animate={{ opacity: [1, 0] }}
                        transition={{ duration: 0.5, repeat: Infinity, repeatType: "reverse" }}
                      />
                    )}
                  </motion.div>
                  <div className="flex justify-end">
                    <motion.div
                      className="px-3 md:px-4 py-1.5 md:py-2 rounded-lg text-xs md:text-sm font-medium flex items-center gap-2"
                      animate={{
                        backgroundColor:
                          animationPhase === "submitted"
                            ? "hsl(var(--primary))"
                            : animationPhase === "hovering"
                            ? "hsl(var(--primary) / 0.9)"
                            : "hsl(var(--primary))",
                        scale:
                          animationPhase === "hovering"
                            ? 1.02
                            : animationPhase === "submitted"
                            ? 0.98
                            : 1,
                      }}
                      transition={{ duration: 0.15, ease: "easeOut" }}
                      style={{ color: "hsl(var(--primary-foreground))" }}
                    >
                      <AnimatePresence mode="wait">
                        {animationPhase === "submitted" ? (
                          <motion.span
                            key="submitted"
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            transition={{ duration: 0.15 }}
                            className="flex items-center gap-1.5"
                          >
                            <Check className="h-3.5 w-3.5" />
                            Submitted
                          </motion.span>
                        ) : (
                          <motion.span
                            key="submit"
                            initial={{ opacity: 0, y: 5 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -5 }}
                            transition={{ duration: 0.15 }}
                          >
                            Submit Ticket
                          </motion.span>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Arrow */}
              <div className="flex justify-center my-4">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={isInView ? { scale: 1 } : {}}
                  transition={{ delay: 0.6 }}
                  className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center"
                >
                  <Bell className="h-5 w-5 text-primary" />
                </motion.div>
              </div>

              {/* Agency Side */}
              <div>
                <div className="text-xs text-muted-foreground mb-3 flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-primary" />
                  Agency Dashboard
                </div>
                <motion.div
                  initial={{ opacity: 0.4 }}
                  animate={{
                    opacity: showDashboardTicket ? 1 : 0.4,
                    y: showDashboardTicket ? 0 : 8,
                  }}
                  transition={{ duration: 0.4, ease: [0.25, 0.46, 0.45, 0.94] }}
                  className="bg-primary/5 rounded-xl p-4 border border-primary/20"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-medium">
                        AC
                      </div>
                      <div>
                        <div className="text-sm font-medium text-foreground">Acme Corp</div>
                        <div className="text-xs text-muted-foreground">Just now</div>
                      </div>
                    </div>
                    <AnimatePresence>
                      {showNewBadge && (
                        <motion.span
                          initial={{ opacity: 0, scale: 0.96 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0, scale: 0.96 }}
                          transition={{ duration: 0.25, ease: [0.25, 0.46, 0.45, 0.94] }}
                          className="px-2 py-1 bg-orange-100 text-orange-600 rounded-md text-xs font-medium"
                        >
                          New
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {showDashboardTicket ? "The AI isn't booking appointments correctly..." : ""}
                  </p>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TicketsSection;
